import { ColorEnum } from '@/constants/color'
import {
    borderRadius,
    shadowOffsetHeight,
    shadowOffsetWidth,
    shadowOpacity,
    shadowRadius,
    visibilityDurationMs,
} from '@/features/common/components/Toast/constants'
import { useHideOnSwipe } from '@/features/common/components/Toast/hooks/useHideOnSwipe'
import CloseButton from '@/features/common/components/Toast/parts/CloseButton'
import CrossInCircleIcon from '@/features/common/components/Toast/parts/CrossInCircleIcon'
import { getExitingAnimation } from '@/features/common/components/Toast/utils/getExitingAnimation'
import { ProgressBar } from '@/features/ui'
import Typography from '@/features/ui/typography'
import { hexToRgba } from '@/utils/hexToRgba'
import { FC } from 'react'
import { Platform, View } from 'react-native'
import { GestureDetector } from 'react-native-gesture-handler'
import Animated, { LightSpeedInLeft } from 'react-native-reanimated'
import { createStyleSheet, useStyles } from 'react-native-unistyles'

interface ToastProps {
    title: string
    message: string
    onHide: () => void
    onExitingAnimationEnd: () => void
}

const Toast: FC<ToastProps> = ({ title, message, onHide, onExitingAnimationEnd }) => {
    const { gesture, wasSwiped, animatedStyle } = useHideOnSwipe(onHide)

    const { styles } = useStyles(stylesheet)

    return (
        <GestureDetector gesture={gesture}>
            <Animated.View
                entering={LightSpeedInLeft}
                exiting={getExitingAnimation(wasSwiped, onExitingAnimationEnd)}
                style={[styles.animatedContainer, animatedStyle]}
            >
                <View style={styles.outerContainer}>
                    <View style={styles.innerContainer}>
                        <CrossInCircleIcon />
                        <View style={{ flex: 1, gap: 4 }}>
                            <Typography.Heading variant="h4" numberOfLines={1} style={styles.text}>
                                {title}
                            </Typography.Heading>
                            <Typography.Description numberOfLines={3} style={styles.text}>
                                {message}
                            </Typography.Description>
                        </View>
                    </View>
                    <ProgressBar
                        duration={visibilityDurationMs}
                        onProgressEnd={onHide}
                        style={styles.progressBar}
                    />
                    <CloseButton onPress={onHide} />
                </View>
            </Animated.View>
        </GestureDetector>
    )
}

const stylesheet = createStyleSheet((_, { insets }) => ({
    animatedContainer: {
        borderRadius,
        position: 'absolute',
        bottom: Math.max(insets.bottom + 16, 64),
        width: '100%',
        maxWidth: 285,
        ...Platform.select({
            ios: {
                shadowColor: ColorEnum.BRICK_700,
                shadowRadius,
                shadowOffset: { height: shadowOffsetHeight, width: shadowOffsetWidth },
                shadowOpacity,
            },
            default: {
                filter: `drop-shadow(${shadowOffsetWidth}px ${shadowOffsetHeight}px ${shadowRadius}px ${hexToRgba(ColorEnum.BRICK_700, shadowOpacity)})`,
            },
            ...(Number(Platform.Version) < 31 && { android: { elevation: 14 } }),
        }),
    },
    outerContainer: {
        backgroundColor: ColorEnum.Jet700,
        borderRadius,
        overflow: 'hidden',
        paddingTop: 12,
        paddingBottom: 13,
    },
    innerContainer: {
        flexDirection: 'row',
        alignItems: 'center',
        paddingHorizontal: 14,
        gap: 10,
    },
    text: {
        color: ColorEnum.WHITE,
    },
    progressBar: {
        backgroundColor: ColorEnum.RED_500,
        position: 'absolute',
        bottom: -1,
        left: 0,
    },
}))

export default Toast
